import React, { useState, useEffect } from 'react';
import {
  TextAppearance,
  Text,
  TextColor,
  TextCustom,
  TextSize,
  TextLineHeight,
  TextWeight,
  TextTag,
  LinkButton,
  DisableFocusWrapper,
  TextType,
} from '@vw-marketing/us-components';
import {
  FadeWrapper,
  CollapseTextContainer,
  CollapseTextWrapper,
  TextWrapper,
  ButtonViewMoreWrapper,
} from './styled';
import { AppConstants } from '../../utils/app-constants';

interface CollapseTextProps {
  readonly text: string;
  readonly lines: number;
  readonly lineHeight: number;
  readonly noFadeTextWrapper?: boolean;
  readonly dynamicLines?: boolean;
  setStateContainer?: (state: boolean) => void;
}
const CollapseText: React.FC<CollapseTextProps> = props => {
  const {
    text,
    lines,
    lineHeight,
    noFadeTextWrapper,
    dynamicLines,
    setStateContainer,
  } = props;
  const [displayedLines, setDisplayedLines] = useState<number | undefined>(
    undefined,
  );
  const [showButton, setShowButton] = useState(true);
  const [showMoreorLess, setShowMoreorLess] = useState(true);
  const paragraphRef = React.useRef<HTMLElement | null>(null);
  const containerRef = React.useRef<HTMLElement | null>(null);

  // Text Constants
  const viewMore = 'View more';
  const viewLess = ' View less';

  // Ref definition
  const textCallback = React.useCallback((element: HTMLElement | null) => {
    paragraphRef.current = element;
  }, []);
  const containerCallback = React.useCallback((element: HTMLElement | null) => {
    containerRef.current = element;
  }, []);

  /**
   * Calculate how many lines fit on container
   * @param height
   * @param paragraphHeight
   */
  const calculateLines = (height?: number, paragraphHeight?: number): void => {
    if (height && paragraphHeight) {
      let containerLines = Math.floor(height / 24);
      let paragraphLines = Math.floor(paragraphHeight / 24);
      if (paragraphLines < containerLines) {
        setShowButton(false);
        containerLines = paragraphLines;
      } else {
        containerLines = containerLines - 2;
      }
      setDisplayedLines(containerLines >= lines ? containerLines : lines);
    }
  };

  /**
   * Decide if display or not the view button regarding with container height overflow
   */
  const getShowButtom = () => {
    if (paragraphRef.current) {
      const paragraph = paragraphRef.current.children[0];
      const overflowText = paragraph.scrollHeight > paragraph.clientHeight;
      setShowButton(overflowText);
      overflowText &&
        dynamicLines &&
        calculateLines(
          (containerRef.current && containerRef.current.clientHeight) ||
            undefined,
          paragraph.scrollHeight,
        );
    }
  };

  /**
   * Display more label and change external handler
   */
  const showMore = () => {
    setShowMoreorLess(!showMoreorLess);
    setStateContainer && setStateContainer(showMoreorLess);
  };

  useEffect(() => {
    showMoreorLess && getShowButtom();
  }, [paragraphRef.current]);

  return (
    <CollapseTextContainer
      dynamicContainer={dynamicLines}
      ref={containerCallback}
    >
      <CollapseTextWrapper>
        <FadeWrapper
          displayAction={showButton && showMoreorLess && !noFadeTextWrapper}
        />
        <TextWrapper
          className={
            text !== AppConstants.DEFAULT_COMMENTS_MESSAGE ? 'notranslate' : ''
          }
          ref={textCallback}
          lines={
            showMoreorLess && showButton ? displayedLines || lines : undefined
          }
          height={
            showMoreorLess && showButton
              ? (displayedLines || lines) * lineHeight
              : undefined
          }
        >
          <TextCustom
            appearance={TextAppearance.headline200}
            style={{
              fontSize: TextSize.TextSize16,
              lineHeight: TextLineHeight.TextLineHeight24,
              color: TextColor.primary,
              fontWeight: TextWeight.regular,
              fontType: TextType.Copy,
            }}
            tag={TextTag.p}
          >
            {text}
          </TextCustom>
        </TextWrapper>
      </CollapseTextWrapper>
      {showButton && (
        <ButtonViewMoreWrapper>
          <Text
            color={TextColor.inherit}
            appearance={TextAppearance.label150}
            bold
          >
            <DisableFocusWrapper>
              <LinkButton onClick={showMore} isInline>
                {showMoreorLess ? viewMore : viewLess}
              </LinkButton>
            </DisableFocusWrapper>
          </Text>
        </ButtonViewMoreWrapper>
      )}
    </CollapseTextContainer>
  );
};

export default CollapseText;
